import { useState } from "react";
import { Box, Checkbox, TableCell, TableRow } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DotLegalMultiSelect, DotLegalTableWithControls, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { DotLegalSelectOption } from "@common/components/dotLegalMultiSelect/DotLegalMultiSelect.types";
import { useTranslation } from "@localization/useTranslation";
import { sortIds } from "@common/utilities";
import FrameworkControlStatusChip from "../../../framework/frameworkOverview/frameworkControlStatusChip/FrameworkControlStatusChip";
import { FrameworkControlStatus } from "../../../framework/frameworks/Frameworks.types";
import { PolicyControl } from "../PolicySections.types";

export interface PolicyControlsPickerProps {
    availableControls: Array<PolicyControl>;
    selectedIds: Array<string>;
    onSelectedIdsChange: (controlIds: Array<string>) => void;
}

interface PolicyControlTableModel {
    id: string;
    customId: string;
    name: string;
    nameWithId: string;
    frameworkName: string;
    status: FrameworkControlStatus;
    statusAsString: string;
    isSelected: boolean;
}

// The framework-control picker table (framework filter, search, select-all, status chip). Selection is fully
// controlled by the parent so it can be hosted either inside PolicyControlsDialog (Save/Cancel modal for the
// policy editor) or inline inside the risk RELATED CONTROLS panel.
function PolicyControlsPicker(props: PolicyControlsPickerProps) {
    const { translateString, translateFrameworkControlStatusEnum } = useTranslation();
    const [selectedFrameworks, setSelectedFrameworks] = useState<Array<string>>([]);

    const frameworkOptions: Array<DotLegalSelectOption> = Array.from(new Set(props.availableControls.map((control) => control.frameworkName)))
        .sort((a, b) => a.localeCompare(b))
        .map((frameworkName) => ({ id: frameworkName, name: frameworkName }));

    const filteredControls =
        selectedFrameworks.length === 0
            ? props.availableControls
            : props.availableControls.filter((control) => selectedFrameworks.includes(control.frameworkName));

    const toggleControl = (id: string) =>
        props.onSelectedIdsChange(props.selectedIds.includes(id) ? props.selectedIds.filter((x) => x !== id) : [...props.selectedIds, id]);

    // Select-all acts on the filtered view: toggle only the visible controls, preserving selections made under other frameworks.
    const onSelectAllChanged = () => {
        const filteredIds = filteredControls.map((control) => control.id);
        const allFilteredSelected = filteredIds.every((id) => props.selectedIds.includes(id));
        props.onSelectedIdsChange(
            allFilteredSelected ? props.selectedIds.filter((id) => !filteredIds.includes(id)) : Array.from(new Set([...props.selectedIds, ...filteredIds]))
        );
    };

    const getHeaderCheckBoxState = (): "true" | "false" | "indeterminate" => {
        const noneSelected = filteredControls.every((control) => !props.selectedIds.includes(control.id));
        if (noneSelected) return "false";

        const allSelected = filteredControls.every((control) => props.selectedIds.includes(control.id));
        return allSelected ? "true" : "indeterminate";
    };

    const data: Array<PolicyControlTableModel> = filteredControls.map((control) => ({
        id: control.id,
        customId: control.customId ?? "",
        name: control.name,
        nameWithId: control.customId ? `${control.customId} ${control.name}` : control.name,
        frameworkName: control.frameworkName,
        status: control.status,
        statusAsString: translateFrameworkControlStatusEnum(control.status.toString()),
        isSelected: props.selectedIds.includes(control.id),
    }));

    const headers: Array<ITableHeader<PolicyControlTableModel>> = [
        {
            property: "frameworkName",
            text: translateString("framework"),
            align: "left",
            showOnMobile: true,
        },
        {
            property: "nameWithId",
            text: translateString("control"),
            align: "left",
            showOnMobile: true,
            sortOverride: (a, b, order) => sortIds(a.customId, b.customId, order === "desc"),
        },
        {
            property: "statusAsString",
            text: translateString("status"),
            align: "left",
            showOnMobile: true,
        },
    ];

    return (
        <DotLegalTableWithControls
            internalScroll
            headers={headers}
            data={data}
            extraControls={
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                    <DotLegalMultiSelect
                        onChange={setSelectedFrameworks}
                        chosenOptions={selectedFrameworks}
                        label={translateString("framework")}
                        placeholder={translateString("framework")}
                        options={frameworkOptions}
                        noMargin
                        noOptionsLabel={translateString("noOptions")}
                    />
                </Grid>
            }
            defaultOrderBy="frameworkName"
            defaultOrder="asc"
            columnsExcludedFromFilter={["id", "frameworkName", "status", "statusAsString", "isSelected"]}
            getUserSpecificPageLength={() => 30}
            hideRowsPerPage
            clickableRows={false}
            onSelectAllChanged={onSelectAllChanged}
            headerCheckBoxState={getHeaderCheckBoxState()}
            emptyText={translateString("noFrameworksMatchesQuery")}
            labelRowsPerPage={translateString("showEntites")}
            labelSearch={translateString("filterByNameOrId")}
            noOptionsLabel={translateString("noOptions")}
            paginationLabelOf={translateString("labelOf")}
            renderRow={(row) => (
                <TableRow key={row.id} hover sx={{ cursor: "pointer" }} onClick={() => toggleControl(row.id)}>
                    <TableCell padding="checkbox">
                        <Checkbox checked={row.isSelected} readOnly tabIndex={-1} />
                    </TableCell>
                    <TableCell>{row.frameworkName}</TableCell>
                    <TableCell>
                        <Box component="span" sx={{ display: "flex", gap: 1 }}>
                            {row.customId && (
                                <Box component="span" sx={{ fontWeight: 600 }}>
                                    {row.customId}
                                </Box>
                            )}
                            {row.name}
                        </Box>
                    </TableCell>
                    <TableCell>
                        <FrameworkControlStatusChip isExcluded={false} status={row.status} value={row.statusAsString} />
                    </TableCell>
                </TableRow>
            )}
        />
    );
}

export default PolicyControlsPicker;
